import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, Check, Plus } from "lucide-react";

import { ApiError, api } from "../api/client";
import { Badge, Button, Card, EmptyState, PageHeader, TableSkeleton } from "../components/ui";
import { formatNumber } from "../lib/format";

const ACCOUNT_TONE: Record<string, "slate" | "sky" | "emerald" | "amber" | "rose"> = {
  trialing: "sky",
  active: "emerald",
  past_due: "amber",
  suspended: "rose",
  cancelled: "slate",
};

/** Superadmin's whole book: provision, watch, suspend, reactivate. */
export function PlatformCompaniesPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");

  const summary = useQuery({
    queryKey: ["platform-summary"],
    queryFn: ({ signal }) => api.platformSummary(signal),
  });

  const companies = useQuery({
    queryKey: ["platform-companies", search],
    queryFn: ({ signal }) => api.platformCompanies({ search: search || undefined, page_size: 50 }, signal),
    placeholderData: (prev) => prev,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["platform-companies"] });
    void queryClient.invalidateQueries({ queryKey: ["platform-summary"] });
  };

  const transition = useMutation({
    mutationFn: ({ id, suspend }: { id: string; suspend: boolean }) =>
      suspend ? api.suspendCompany(id) : api.activateCompany(id),
    onSuccess: invalidate,
  });

  const rows = companies.data?.results ?? [];

  return (
    <>
      <PageHeader
        title="Companies"
        subtitle="Every account on the platform. Provisioning is one call; suspending is deliberate."
        actions={<ProvisionForm />}
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryStat label="Accounts" value={summary.data?.accounts_total} pending={summary.isPending} />
        <SummaryStat label="Active" value={summary.data?.accounts_active} pending={summary.isPending} tone="emerald" />
        <SummaryStat label="Trialing" value={summary.data?.accounts_trialing} pending={summary.isPending} tone="sky" />
        <SummaryStat label="Suspended" value={summary.data?.accounts_suspended} pending={summary.isPending} tone="rose" />
      </div>

      <label className="mb-4 block sm:max-w-xs">
        <span className="sr-only">Search companies</span>
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by name or slug…"
          className="w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
        />
      </label>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
        {companies.isPending ? (
          <TableSkeleton rows={6} />
        ) : companies.isError ? (
          <EmptyState title="Could not load companies" body="Please try again." />
        ) : rows.length === 0 ? (
          <EmptyState title={search ? "Nothing matches" : "No companies yet"} body={search ? "Try a different search." : "Provision the first account to start the book."} />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5">Company</th>
                <th className="px-4 py-2.5">Plan</th>
                <th className="px-4 py-2.5">Seats</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Owner</th>
                <th className="px-4 py-2.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((account) => (
                <tr key={account.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-slate-900">{account.name}</p>
                    <p className="text-xs text-slate-500">{account.slug}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{account.plan_name}</td>
                  <td className="px-4 py-3 text-sm tabular-nums text-slate-700">{formatNumber(account.user_count)}</td>
                  <td className="px-4 py-3">
                    <Badge tone={ACCOUNT_TONE[account.status ?? "trialing"]}>{account.status ?? "—"}</Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{account.owner_name || "—"}</td>
                  <td className="px-4 py-3 text-right" onClick={(event) => event.stopPropagation()}>
                    {account.status === "suspended" ? (
                      <Button variant="secondary" disabled={transition.isPending} onClick={() => transition.mutate({ id: account.id, suspend: false })}>
                        <Check size={14} /> Activate
                      </Button>
                    ) : (
                      <Button variant="ghost" disabled={transition.isPending} onClick={() => transition.mutate({ id: account.id, suspend: true })}>
                        <Ban size={14} /> Suspend
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function SummaryStat({
  label,
  value,
  pending,
  tone,
}: {
  label: string;
  value: number | undefined;
  pending: boolean;
  tone?: "emerald" | "sky" | "rose";
}) {
  return (
    <Card className="flex items-center justify-between">
      <p className="text-sm text-slate-500">{label}</p>
      {pending ? (
        <span className="h-5 w-10 animate-pulse rounded bg-slate-100" />
      ) : (
        <p className={`text-lg font-semibold tabular-nums ${tone ? textTone[tone] : "text-slate-900"}`}>
          {formatNumber(value ?? 0)}
        </p>
      )}
    </Card>
  );
}

const textTone = {
  emerald: "text-emerald-600",
  sky: "text-sky-600",
  rose: "text-rose-600",
};

function ProvisionForm() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [industry, setIndustry] = useState("");

  const industries = useQuery({
    queryKey: ["industries"],
    queryFn: ({ signal }) => api.industries(signal),
  });

  const provision = useMutation({
    mutationFn: () =>
      api.provisionCompany({
        business_name: businessName.trim(),
        owner_email: ownerEmail.trim(),
        industry: Number(industry),
      }),
    onSuccess: () => {
      setOpen(false);
      setBusinessName("");
      setOwnerEmail("");
      setIndustry("");
      void queryClient.invalidateQueries({ queryKey: ["platform-companies"] });
      void queryClient.invalidateQueries({ queryKey: ["platform-summary"] });
      window.alert(
        "Company created. A password setup email has been sent to the owner.",
      );
    },
  });

  if (!open) {
    return (
      <Button variant="primary" onClick={() => setOpen(true)}>
        <Plus size={15} /> Provision
      </Button>
    );
  }

  return (
    <Card padded={false} className="w-full max-w-sm p-4">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!businessName.trim() || !ownerEmail.trim() || !industry) return;
          provision.mutate();
        }}
        className="grid gap-3"
      >
        <p className="text-sm font-semibold text-slate-900">New company</p>
        <input
          value={businessName}
          onChange={(event) => setBusinessName(event.target.value)}
          required
          placeholder="Business name"
          aria-label="Business name"
          className="w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
        />
        <input
          type="email"
          value={ownerEmail}
          onChange={(event) => setOwnerEmail(event.target.value)}
          required
          placeholder="Owner email"
          aria-label="Owner email"
          className="w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
        />
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium text-slate-600">Industry</span>
          <select
            value={industry}
            onChange={(event) => setIndustry(event.target.value)}
            required
            className="w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300"
          >
            <option value="">Choose…</option>
            {(industries.data ?? []).map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>
        </label>
        {provision.error ? (
          <p role="alert" className="text-sm text-rose-600">
            {provision.error instanceof ApiError ? provision.error.message : "Provisioning failed."}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={provision.isPending}>
            Create
          </Button>
        </div>
      </form>
    </Card>
  );
}